import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Buildings, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const inputCls = "rounded-none bg-secondary border-border focus-visible:ring-primary";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

export default function CompaniesPanel() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [search, setSearch] = useState([]);

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/admin/companies")).data });

  const inval = () => { qc.invalidateQueries({ queryKey: ["companies"] }); qc.invalidateQueries({ queryKey: ["admin-users"] }); };

  const saveMut = useMutation({
    mutationFn: async ({ mode, id, body }) => (mode === "create" ? api.post("/admin/companies", body) : api.patch(`/admin/companies/${id}`, body)),
    onSuccess: (res) => { inval(); setDialog((d) => d ? { ...d, mode: "edit", data: res.data } : d); toast.success("Company saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/admin/companies/${id}`), onSuccess: () => { inval(); toast.success("Company deleted"); } });
  const enrichMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/companies/${id}/enrich`),
    onSuccess: (res) => { inval(); setDialog((d) => d ? { ...d, data: res.data } : d); toast.success("Enriched from Brreg"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Brreg lookup failed"),
  });
  const searchMut = useMutation({
    mutationFn: async (q) => (await api.get(`/admin/brreg/search?q=${encodeURIComponent(q)}`)).data,
    onSuccess: (data) => setSearch(data),
    onError: () => toast.error("Brreg search failed"),
  });

  const save = () => {
    const d = dialog.data;
    const body = { name: d.name, org_number: d.org_number || "", website: d.website || "" };
    saveMut.mutate({ mode: dialog.mode, id: d.id, body });
  };

  return (
    <div className="space-y-4" data-testid="companies-panel">
      <div className="flex justify-end">
        <Button onClick={() => { setSearch([]); setDialog({ mode: "create", data: {} }); }} data-testid="add-company-button" className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
          <Plus size={16} className="mr-1" /> New company
        </Button>
      </div>

      <div className="border border-border divide-y divide-border">
        {companies.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-4" data-testid={`company-${c.id}`}>
            <Buildings size={20} className="text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{c.name}</p>
              <div className="flex flex-wrap gap-2 mt-1 font-mono text-[10px] text-muted-foreground">
                {c.org_number ? <span className="text-primary">#{c.org_number}</span> : <span className="text-amber-700">no org number</span>}
                {c.industry_text && <span>· {c.industry_text}</span>}
                {c.status && <span>· {c.status}</span>}
                <span>· {c.contact_count} contact{c.contact_count === 1 ? "" : "s"}</span>
              </div>
            </div>
            <button onClick={() => { setSearch([]); setDialog({ mode: "edit", data: { ...c } }); }} data-testid={`edit-company-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={18} /></button>
            <button onClick={() => { if (window.confirm(`Delete "${c.name}"? Its ${c.contact_count} contact(s) will be unlinked.`)) delMut.mutate(c.id); }} data-testid={`delete-company-${c.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={18} /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg max-h-[88vh] overflow-y-auto" data-testid="company-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl font-normal">{dialog?.mode === "create" ? "New company" : dialog?.data?.name}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <Field label="Company name">
                <Input data-testid="company-name-input" className={inputCls} value={dialog.data.name || ""} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })} />
              </Field>
              <Field label="Organisasjonsnummer">
                <div className="flex gap-2">
                  <Input data-testid="company-org-input" className={inputCls} placeholder="9 digits" value={dialog.data.org_number || ""} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, org_number: e.target.value } })} />
                  {dialog.mode === "edit" && (
                    <Button data-testid="company-enrich-button" disabled={!dialog.data.org_number || enrichMut.isPending} onClick={() => enrichMut.mutate(dialog.data.id)} className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs shrink-0">
                      <ArrowClockwise size={16} className={`mr-1 ${enrichMut.isPending ? "animate-spin" : ""}`} /> Brreg
                    </Button>
                  )}
                </div>
              </Field>

              {/* Brreg search helper */}
              <Field label="Find org number by name (Brreg)">
                <div className="flex gap-2">
                  <Input data-testid="brreg-search-input" className={inputCls} placeholder="Type a company name…" onKeyDown={(e) => { if (e.key === "Enter") searchMut.mutate(e.target.value); }} defaultValue={dialog.data.name || ""} id="brreg-q" />
                  <Button data-testid="brreg-search-button" onClick={() => searchMut.mutate(document.getElementById("brreg-q").value)} disabled={searchMut.isPending} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs shrink-0">
                    <MagnifyingGlass size={16} />
                  </Button>
                </div>
              </Field>
              {search.length > 0 && (
                <div className="border border-border divide-y divide-border" data-testid="brreg-results">
                  {search.map((s) => (
                    <button key={s.org_number} onClick={() => { setDialog({ ...dialog, data: { ...dialog.data, org_number: s.org_number, name: dialog.data.name || s.name } }); setSearch([]); }} className="w-full text-left p-3 hover:bg-secondary transition-colors" data-testid={`brreg-result-${s.org_number}`}>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">#{s.org_number} · {s.org_form}</p>
                    </button>
                  ))}
                </div>
              )}

              {/* Enriched data */}
              {(dialog.data.legal_name || dialog.data.address) && (
                <div className="border border-border p-4 bg-secondary/40 space-y-1 text-sm font-light" data-testid="company-brreg-data">
                  <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Brreg data</p>
                  {dialog.data.legal_name && <p><span className="text-muted-foreground">Legal name:</span> {dialog.data.legal_name}</p>}
                  {dialog.data.org_form && <p><span className="text-muted-foreground">Org form:</span> {dialog.data.org_form}</p>}
                  {dialog.data.status && <p><span className="text-muted-foreground">Status:</span> {dialog.data.status}</p>}
                  {dialog.data.industry_text && <p><span className="text-muted-foreground">NACE {dialog.data.industry_code}:</span> {dialog.data.industry_text}</p>}
                  {dialog.data.address && <p><span className="text-muted-foreground">Address:</span> {dialog.data.address}</p>}
                </div>
              )}

              <Field label="Website">
                <Input data-testid="company-website-input" className={inputCls} value={dialog.data.website || ""} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, website: e.target.value } })} />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-company-button" disabled={!dialog?.data?.name || saveMut.isPending} onClick={save} className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
